import React, { useState } from 'react';
import Layout from '../../components/admin/Layout';
import DataTable from '../../components/admin/DataTable';
import DetailDrawer from '../../components/admin/DetailDrawer';
import { Button, Field, StatusBadge, formatDate, relativeTime } from '../../components/admin/ui';
import useAdminResource from '../../hooks/useAdminResource';

const STATUSES = ['new', 'read', 'replied', 'archived'];

const Contacts = () => {
  const controller = useAdminResource({ resource: 'contacts' });
  const [active, setActive] = useState(null);

  const openRow = async (row) => {
    setActive(row);
    // Opening an unread enquiry marks it read, mirroring an inbox.
    if (row.status === 'new') {
      await controller.updateRow(row.id, { status: 'read', isRead: true }, 'Marked as read');
      setActive({ ...row, status: 'read', isRead: true });
    }
  };

  const applyStatus = async (status) => {
    if (!active) return;
    const ok = await controller.updateRow(active.id, { status }, 'Contact updated');
    if (ok) setActive({ ...active, status });
  };

  const columns = [
    {
      key: 'name',
      label: 'From',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className={`h-2 w-2 flex-shrink-0 rounded-full ${row.status === 'new' ? 'bg-indigo-500' : 'bg-transparent'}`} />
          <div className="min-w-0">
            <p className={`truncate ${row.status === 'new' ? 'font-semibold text-slate-900' : 'font-medium text-slate-700'}`}>
              {row.name}
            </p>
            <a
              href={`mailto:${row.email}`}
              onClick={(e) => e.stopPropagation()}
              className="block truncate text-xs text-indigo-600 hover:underline"
            >
              {row.email}
            </a>
          </div>
        </div>
      )
    },
    {
      key: 'message',
      label: 'Message',
      className: 'max-w-md',
      render: (row) => (
        <div className="min-w-0">
          {row.subject && <p className="truncate font-medium text-slate-800">{row.subject}</p>}
          <p className="truncate text-slate-500">{row.message}</p>
        </div>
      )
    },
    {
      key: 'phone',
      label: 'Phone',
      className: 'whitespace-nowrap',
      render: (row) => (row.phone ? (
        <a
          href={`tel:${row.phone}`}
          onClick={(e) => e.stopPropagation()}
          className="text-slate-600 hover:text-slate-900"
        >
          {row.phone}
        </a>
      ) : <span className="text-slate-300">—</span>)
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (row) => <StatusBadge status={row.status} />
    },
    {
      key: 'createdAt',
      label: 'Received',
      sortable: true,
      className: 'whitespace-nowrap',
      render: (row) => (
        <span className="text-slate-500" title={formatDate(row.createdAt, true)}>
          {relativeTime(row.createdAt)}
        </span>
      )
    }
  ];

  return (
    <Layout>
      <DataTable
        title="Contacts"
        description="Enquiries from the website contact form"
        icon="✉️"
        controller={controller}
        columns={columns}
        statuses={STATUSES}
        bulkStatuses={STATUSES}
        onRowClick={openRow}
        searchPlaceholder="Search name, email, subject, message…"
        emptyTitle="No enquiries yet"
        emptyDescription="Messages from the contact form will appear here."
      />

      <DetailDrawer
        open={Boolean(active)}
        onClose={() => setActive(null)}
        title={active?.name || ''}
        subtitle={active?.subject || 'Contact enquiry'}
        status={active?.status}
        statuses={STATUSES}
        onStatusChange={applyStatus}
        footer={active && (
          <Button
            variant="primary"
            onClick={() => {
              const subject = encodeURIComponent(`Re: ${active.subject || 'Your enquiry'}`);
              window.location.href = `mailto:${active.email}?subject=${subject}`;
            }}
          >
            Reply by email
          </Button>
        )}
      >
        {active && (
          <div className="space-y-6">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-4">
              <Field label="Name">{active.name}</Field>
              <Field label="Received">{formatDate(active.createdAt, true)}</Field>
              <Field label="Email">
                <a href={`mailto:${active.email}`} className="text-indigo-600 hover:underline">
                  {active.email}
                </a>
              </Field>
              <Field label="Phone">
                {active.phone && (
                  <a href={`tel:${active.phone}`} className="text-indigo-600 hover:underline">
                    {active.phone}
                  </a>
                )}
              </Field>
              <Field label="Subject" className="col-span-2">{active.subject}</Field>
            </dl>

            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                Message
              </h3>
              <div className="whitespace-pre-wrap rounded-xl bg-slate-50 p-4 text-sm leading-relaxed text-slate-700">
                {active.message}
              </div>
            </section>

            {active.repliedAt && (
              <p className="text-xs text-slate-400">
                Marked replied on {formatDate(active.repliedAt, true)}
              </p>
            )}
          </div>
        )}
      </DetailDrawer>
    </Layout>
  );
};

export default Contacts;
